/**
 * ApiKeysSettings. centralized API key management for System Admins.
 * Reads/writes from the AppSettings table via /api/app-settings.
 * All keys are masked by default; reveal with the eye icon.
 */
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, TextField, Button, Chip, Divider,
  InputAdornment, IconButton, Alert, CircularProgress, Snackbar,
} from "@mui/material";
import VisibilityIcon     from "@mui/icons-material/Visibility";
import VisibilityOffIcon  from "@mui/icons-material/VisibilityOff";
import KeyIcon            from "@mui/icons-material/Key";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import api from "../../utils/api";

const API_KEYS = [
  {
    key:         "mapbox_token",
    label:       "Mapbox API Token",
    description: "Used for satellite map tiles in Marina Map. Free tier: 50,000 loads/month.",
    placeholder: "pk.eyJ1...",
    helpUrl:     "https://mapbox.com",
    helpLabel:   "Get a free Mapbox token",
    module:      "Marina Map",
  },
  {
    key:         "ANTHROPIC_API_KEY",
    label:       "Anthropic API Key",
    description: "Powers the HOA AI Assistant (Claude). Required for AI Chat to work.",
    placeholder: "sk-ant-api03-...",
    helpUrl:     "https://console.anthropic.com",
    helpLabel:   "Get an Anthropic API key",
    module:      "AI Chat",
    isEnv:       true, // stored in .env, not AppSettings. show read-only status
  },
];

function KeyRow({ keyDef, value, onChange, saving }) {
  const [show, setShow] = useState(false);
  const [localVal, setLocalVal] = useState(value || "");

  useEffect(() => { setLocalVal(value || ""); }, [value]);

  const isSet = (localVal || "").length > 10;
  const masked = isSet ? localVal.substring(0, 6) + "••••••••••••" + localVal.slice(-4) : "";

  return (
    <Paper elevation={0} sx={{ p:2.5, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.10)", mb:2 }}>
      <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", mb:1 }}>
        <Box sx={{ flex:1 }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.25 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{keyDef.label}</Typography>
            <Chip label={keyDef.module} size="small" sx={{ height:18, fontSize:"0.6rem", fontWeight:600, backgroundColor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} />
            {isSet && <CheckCircleIcon sx={{ fontSize:16, color:"#16a34a" }} />}
          </Box>
          <Typography variant="caption" sx={{ color:"#6b778c" }}>{keyDef.description}</Typography>
        </Box>
      </Box>

      {keyDef.isEnv ? (
        <Alert severity={isSet ? "success" : "warning"} sx={{ borderRadius:"10px", fontSize:"0.75rem" }}>
          {isSet
            ? `API key is configured (ends in …${localVal.slice(-4)})`
            : "Not configured. Contact your system administrator to set up the API key."}
        </Alert>
      ) : (
        <Box sx={{ display:"flex", gap:1, mt:1.5 }}>
          <TextField
            fullWidth size="small"
            type={show ? "text" : "password"}
            placeholder={keyDef.placeholder}
            value={localVal}
            onChange={e => setLocalVal(e.target.value)}
            InputProps={{
              sx: { borderRadius:"10px", fontFamily:"monospace", fontSize:"0.82rem" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShow(s=>!s)}>
                    {show ? <VisibilityOffIcon sx={{ fontSize:18 }} /> : <VisibilityIcon sx={{ fontSize:18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" disabled={saving || localVal === value}
            onClick={() => onChange(keyDef.key, localVal)}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, background:"#2f63ad", whiteSpace:"nowrap",
              "&:hover":{ background:"#1e4c8a" }, "&.Mui-disabled":{ opacity:0.5 } }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : "Save"}
          </Button>
        </Box>
      )}

      <Box sx={{ mt:1 }}>
        <Typography component="a" href={keyDef.helpUrl} target="_blank" rel="noopener"
          variant="caption" sx={{ color:"#2f63ad", textDecoration:"none", "&:hover":{ textDecoration:"underline" }, fontSize:"0.7rem" }}>
          🔗 {keyDef.helpLabel} ↗
        </Typography>
      </Box>
    </Paper>
  );
}

export default function ApiKeysSettings() {
  const [values,  setValues]  = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState("");
  const [snack,   setSnack]   = useState({ open:false, msg:"", sev:"success" });

  useEffect(() => {
    Promise.all([
      api.get("/app-settings"),
      // Check Anthropic key status via a lightweight endpoint
      api.get("/ai-chat/access").catch(() => null),
    ]).then(([cfg, access]) => {
      const v = { ...cfg.data };
      // Show partial Anthropic key if accessible
      if (access?.data?.allowed !== false) {
        v.ANTHROPIC_API_KEY = process.env.ANTHROPIC_API_KEY || "sk-ant-api03-••••••••••••";
      }
      setValues(v);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (key, value) => {
    setSaving(key);
    try {
      await api.put("/app-settings", { [key]: value });
      setValues(v => ({ ...v, [key]: value }));
      setSnack({ open:true, msg:`${key} saved successfully`, sev:"success" });
    } catch (e) {
      setSnack({ open:true, msg:"Failed to save key", sev:"error" });
    } finally { setSaving(""); }
  };

  if (loading) return <Box sx={{ py:4, textAlign:"center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:3 }}>
        <KeyIcon sx={{ color:"#2f63ad", fontSize:26 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>API Keys</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Manage API keys for external services. Keys are stored securely and masked by default.
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ borderRadius:"12px", mb:3, fontSize:"0.78rem" }}>
        Only System Admins can view or modify API keys. Keys are stored in the database (AppSettings)
        except for server-side keys which must be configured by the system administrator.
      </Alert>

      <Divider sx={{ mb:3 }} />

      {API_KEYS.map(keyDef => (
        <KeyRow
          key={keyDef.key}
          keyDef={keyDef}
          value={values[keyDef.key] || ""}
          onChange={handleSave}
          saving={saving === keyDef.key}
        />
      ))}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s=>({...s,open:false}))}
        anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"10px" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
